import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { StatusPill } from "../components/StatusPill";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, PencilLine, Users2, Search, ShieldAlert, MoreVertical, Archive, ArchiveRestore, FolderKanban, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import SalaryDialog from "../components/SalaryDialog";
import { canSeeCosts } from "../lib/services";

const ROLES = [
  { key: "account_manager", label: "Account Manager", group: "SMM" },
  { key: "graphic_designer", label: "Graphic Designer", group: "SMM" },
  { key: "content_writer", label: "Content Writer", group: "SMM" },
  { key: "execution_specialist", label: "Execution Specialist", group: "SMM" },
  { key: "project_manager", label: "Project Manager", group: "Dev" },
  { key: "developer", label: "Developer", group: "Dev" },
  { key: "tester", label: "Tester", group: "Dev" },
  { key: "ceo", label: "CEO", group: "Sales" },
  { key: "sales_head", label: "Sales Head", group: "Sales" },
  { key: "sales_member", label: "Sales Member", group: "Sales" },
  { key: "coo", label: "Chief Operating Officer", group: "Admin" },
];
const CLIENT_ROLES = new Set(["account_manager", "graphic_designer", "content_writer", "execution_specialist", "sales_head", "sales_member"]);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const ROLE_LABELS = Object.fromEntries(ROLES.map(r => [r.key, r.label]));
ROLE_LABELS.admin = "Agency Admin";
ROLE_LABELS.manager = "Manager";
ROLE_LABELS.coo = "Chief Operating Officer";
ROLE_LABELS.client = "Client";

const DEV_ROLES = new Set(["developer", "tester", "project_manager"]);

function AssignedProjects({ uid, list }) {
  return (
    <div className="mt-4 pt-4 border-t border-[#E4E7EC]" data-testid={`assigned-projects-${uid}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">
        <FolderKanban className="w-3 h-3"/> Assigned Projects ({list.length})
      </div>
      {list.length === 0 ? (
        <div className="text-[11px] font-mono text-neutral-500">No projects assigned yet</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {list.map(p => (
            <Link key={p.id} to={`/projects/${p.id}`} className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#CBD1DC] bg-[#FFFFFF] hover:border-orange-500/50 hover:text-orange-700 transition-colors" data-testid={`assigned-project-${uid}-${p.id}`}>
              {p.project_name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const GROUPS = ["All", "SMM", "Dev", "Sales", "Admin"];
const TABS = [
  { key: "active", label: "Active", param: "" },
  { key: "archived", label: "Archived", param: "?only_archived=true" },
];

function fmtDate(iso) { if (!iso) return ""; try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso.slice(0, 10); } }

export default function Team() {
  const [salaryOpen, setSalaryOpen] = useState(false);
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [target, setTarget] = useState(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");
  const [tab, setTab] = useState("active");

  const load = async () => {
    const paramMap = Object.fromEntries(TABS.map(t => [t.key, t.param]));
    const [u, c, p] = await Promise.all([api.get(`/users${paramMap[tab]}`), api.get("/clients").catch(() => ({ data: [] })), api.get("/projects").catch(() => ({ data: [] }))]);
    setUsers(u.data); setClients(c.data); setProjects(p.data);
  };
  useEffect(()=>{ load(); }, [tab]);

  const clientName = (id) => clients.find(c=>c.id===id)?.client_name || "";
  const projectsOf = (uid) => {
    const viaClient = new Set(clients.filter(c => c.assigned_project_manager === uid).map(c => c.id));
    return projects.filter(p => p.project_manager_id === uid || (p.development_team || []).includes(uid) || (p.task_assignees || []).includes(uid) || viaClient.has(p.client_id));
  };
  const groupOf = (role) => {
    if (["admin", "coo", "manager"].includes(role)) return "Admin";
    const r = ROLES.find(x => x.key === role);
    return r ? r.group : "Other";
  };

  const filtered = useMemo(() => users.filter(u => {
    const matchQ = !q || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase());
    const matchG = group === "All" || groupOf(u.role) === group;
    return matchQ && matchG;
  }), [users, q, group]);

  const counts = useMemo(() => {
    const c = { All: users.length, SMM: 0, Dev: 0, Sales: 0, Admin: 0 };
    users.forEach(u => { const g = groupOf(u.role); if (c[g] !== undefined) c[g] += 1; });
    return c;
  }, [users]);

  const canManage = ["admin", "ceo", "coo"].includes(user?.role);

  const archive = async (u) => { try { await api.post(`/users/${u.id}/archive`); toast.success(`${u.name} archived — login paused`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unarchive = async (u) => { try { await api.post(`/users/${u.id}/unarchive`); toast.success(`${u.name} restored`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">People</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Team</h1>
          <p className="text-neutral-600 text-sm mt-1">{users.length} {tab === "archived" ? "archived" : "active"} · {counts.SMM} SMM · {counts.Dev} Dev · {counts.Sales} Sales</p>
        </div>
        {canManage && tab === "active" && (
          <div className="flex gap-2">
          {canSeeCosts(user) && <Button variant="outline" onClick={()=>setSalaryOpen(true)} data-testid="team-salaries-btn"><Wallet className="w-4 h-4 mr-1"/> Salaries</Button>}
          <Button onClick={()=>setAddOpen(true)} data-testid="add-team-member-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1"/> Add Team Member
          </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name, email, role..." data-testid="team-search-input" className="pl-9 bg-[#FFFFFF] border-[#E4E7EC]"/>
        </div>
        <div className="flex gap-1">
          {TABS.map(t => (
            <button key={t.key} onClick={()=>setTab(t.key)} data-testid={`team-tab-${t.key}`} className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider border transition-colors ${tab === t.key ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 flex-wrap">
          {GROUPS.map(g => (
            <button key={g} onClick={()=>setGroup(g)} data-testid={`team-group-${g.toLowerCase()}`} className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider border transition-colors ${group === g ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {g} <span className="text-neutral-600 ml-1">{counts[g]}</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="panel p-10 text-center text-neutral-500">
          <Users2 className="w-8 h-8 mx-auto mb-3 opacity-50"/>
          {tab === "archived" ? "No archived team members." : (users.length === 0 ? "No team members yet." : "No members match your filters.")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {filtered.map(u => {
            const isSelf = u.id === user?.id;
            const isArchived = !!u.archived_at;
            return (
              <div key={u.id} className={`panel p-6 min-w-0 ${isArchived ? "opacity-70" : ""}`} data-testid={`team-card-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/40 to-cyan-500/30 grid place-items-center font-bold shrink-0">
                    {(u.name||"?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2 min-w-0">
                      <span className="truncate" title={u.name} data-testid={`team-name-${u.id}`}>{u.name}</span>
                      {isSelf && <span className="shrink-0 text-[9px] font-mono uppercase text-orange-700 bg-orange-500/10 border border-orange-500/30 rounded px-1.5 py-0.5">You</span>}
                      {isArchived && <span className="shrink-0 text-[9px] font-mono uppercase text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded px-1.5 py-0.5">Archived</span>}
                    </div>
                    <div className="text-[11px] font-mono text-neutral-500 truncate" title={u.email}>{u.email}</div>
                    <div className="mt-1.5" data-testid={`team-role-${u.id}`}><StatusPill status={ROLE_LABELS[u.role] || u.role}/></div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 self-start">
                    {canManage && !isSelf && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded-md hover:bg-black/[0.04]" data-testid={`user-menu-${u.id}`}>
                            <MoreVertical className="w-4 h-4 text-neutral-600"/>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#FFFFFF] border-[#E4E7EC]">
                          <DropdownMenuItem onClick={()=>setEditTarget(u)} data-testid={`edit-user-${u.id}`} className="cursor-pointer">
                            <PencilLine className="w-3.5 h-3.5 mr-2"/> Edit details
                          </DropdownMenuItem>
                          {!isArchived ? (
                            <DropdownMenuItem onClick={()=>archive(u)} data-testid={`archive-user-${u.id}`} className="text-yellow-700 focus:bg-yellow-500/10 focus:text-yellow-800 cursor-pointer">
                              <Archive className="w-3.5 h-3.5 mr-2"/> Archive (pause login)
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={()=>unarchive(u)} data-testid={`unarchive-user-${u.id}`} className="text-emerald-700 focus:bg-emerald-500/10 focus:text-emerald-800 cursor-pointer">
                              <ArchiveRestore className="w-3.5 h-3.5 mr-2"/> Restore access
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-[#E4E7EC]"/>
                          <DropdownMenuItem onClick={()=>setDeleteTarget(u)} data-testid={`delete-user-${u.id}`} className="text-rose-700 focus:bg-rose-500/10 focus:text-rose-700 cursor-pointer">
                            <Trash2 className="w-3.5 h-3.5 mr-2"/> Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {DEV_ROLES.has(u.role) ? (
                  <AssignedProjects uid={u.id} list={projectsOf(u.id)}/>
                ) : (
                <div className="mt-4 pt-4 border-t border-[#E4E7EC]">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">
                    <Users2 className="w-3 h-3"/> Assigned Clients ({(u.assigned_clients||[]).length})
                  </div>
                  {["admin", "manager"].includes(u.role) ? (
                    <div className="text-[11px] font-mono text-neutral-500">Full agency access</div>
                  ) : !CLIENT_ROLES.has(u.role) ? (
                    <div className="text-[11px] font-mono text-neutral-500">No per-client assignment</div>
                  ) : (u.assigned_clients||[]).length === 0 ? (
                    <div className="text-[11px] font-mono text-neutral-500">No clients assigned yet</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(u.assigned_clients||[]).map(cid => (
                        <span key={cid} className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#CBD1DC] bg-[#FFFFFF]" data-testid={`assigned-${u.id}-${cid}`}>
                          {clientName(cid)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {isArchived && (
                  <div className="mt-3 text-[10px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
                    Login paused since {fmtDate(u.archived_at)} · history preserved
                  </div>
                )}

                {canManage && !isSelf && !isArchived && CLIENT_ROLES.has(u.role) && (
                  <div className="mt-4">
                    <Button size="sm" variant="outline" className="border-[#E4E7EC] w-full" onClick={()=>{ setTarget(u); setAssignOpen(true); }} data-testid={`edit-assign-${u.id}`}>
                      <PencilLine className="w-3.5 h-3.5 mr-1"/> Edit Clients
                    </Button>
                  </div>
                )}
                {canManage && isSelf && (
                  <div className="mt-4 text-[10px] font-mono text-neutral-500 italic">You cannot archive or remove yourself.</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} clients={clients} onSaved={load}/>
      <AssignClientsDialog open={assignOpen} onOpenChange={setAssignOpen} user={target} clients={clients} onSaved={load}/>
      <DeleteMemberDialog target={deleteTarget} onClose={()=>setDeleteTarget(null)} onDeleted={load}/>
      <EditMemberDialog target={editTarget} onClose={()=>setEditTarget(null)} onSaved={load}/>
      {canSeeCosts(user) && <SalaryDialog open={salaryOpen} onOpenChange={setSalaryOpen}/>}
    </div>
  );
}

function DeleteMemberDialog({ target, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!target) return;
    setBusy(true);
    try { await api.delete(`/users/${target.id}`); toast.success(`${target.name} removed`); onClose(); onDeleted?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="delete-member-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-rose-700"><ShieldAlert className="w-5 h-5"/> Remove team member?</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            {target && <>Removing <strong className="text-neutral-800">{target.name}</strong> ({ROLE_LABELS[target.role]}) will also:</>}
          </DialogDescription>
        </DialogHeader>
        <ul className="text-xs text-neutral-600 space-y-1.5 pl-5 list-disc">
          <li>Unassign them from every client, project, requirement, task, and lead</li>
          <li>Revoke their login immediately</li>
          <li>Preserve all their historical activity (comments, uploads, sent proposals)</li>
        </ul>
        <div className="text-[11px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mt-2">Prefer Archive — same effect but reversible.</div>
        <div className="text-[11px] font-mono text-rose-700 bg-rose-500/10 border border-rose-500/30 rounded p-2">This action cannot be undone.</div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="delete-cancel">Cancel</Button>
          <Button onClick={remove} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-neutral-900" data-testid="delete-confirm">
            {busy ? "Removing..." : "Remove Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMemberDialog({ open, onOpenChange, clients, onSaved }) {
  const [form, setForm] = useState({ name:"", email:"", password:"Team@12345", role:"content_writer", client_ids: [], send_invite_email: true });
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ if (open) setForm({ name:"", email:"", password:"Team@12345", role:"content_writer", client_ids: [], send_invite_email: true }); }, [open]);

  const toggle = (cid) => setForm(f => ({...f, client_ids: f.client_ids.includes(cid) ? f.client_ids.filter(x=>x!==cid) : [...f.client_ids, cid]}));
  const supportsClients = CLIENT_ROLES.has(form.role);

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    if (!EMAIL_RE.test(form.email.trim())) { toast.error("Please enter a valid email address."); return; }
    if (!form.password || form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const payload = { ...form, client_ids: supportsClients ? form.client_ids : [] };
      const { data } = await api.post("/users", payload);
      if (form.send_invite_email) {
        if (data?.invited_via_email) toast.success(`Team member added. Invite email sent to ${form.email}.`);
        else toast.success("Team member added. (Invite email could not be sent — check backend logs.)");
      } else {
        toast.success("Team member added");
      }
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    ROLES.forEach(r => { (g[r.group] = g[r.group] || []).push(r); });
    return g;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-xl max-h-[85vh] overflow-y-auto" data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Add Team Member</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">They will receive a login using the credentials below.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Name" required><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="tm-name" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Email" required><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="tm-email" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Password"><Input value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="tm-password" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Role">
            <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="tm-role"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                {Object.entries(grouped).map(([g, roles]) => (
                  <div key={g}>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-neutral-500 px-2 py-1">{g}</div>
                    {roles.map(r=><SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </F>
        </div>
        {supportsClients && (
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Assign Clients ({form.client_ids.length})</Label>
            <div className="mt-2 rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] max-h-60 overflow-y-auto divide-y divide-[#E4E7EC]">
              {clients.length === 0 && <div className="p-3 text-xs text-neutral-500">No clients yet.</div>}
              {clients.map(c => (
                <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-white/[0.02] cursor-pointer" data-testid={`tm-client-${c.id}`}>
                  <Checkbox checked={form.client_ids.includes(c.id)} onCheckedChange={()=>toggle(c.id)}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.client_name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 uppercase">{c.industry} · {c.package}</div>
                  </div>
                  <StatusPill status={c.status}/>
                </label>
              ))}
            </div>
          </div>
        )}
        <label className="flex items-start gap-2 mt-1 cursor-pointer p-3 rounded-lg border border-[#E4E7EC] bg-[#F5F7FB] hover:border-[#CBD1DC]" data-testid="tm-send-invite-toggle">
          <Checkbox checked={form.send_invite_email} onCheckedChange={(v)=>setForm({...form, send_invite_email: !!v})} className="mt-0.5"/>
          <div>
            <div className="text-sm font-semibold">Email login credentials to {form.email || "them"}</div>
            <div className="text-[11px] text-neutral-600">A welcome email with a sign-in link and the temporary password above will be sent immediately. They can change it after their first login.</div>
          </div>
        </label>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="tm-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name || !form.email} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="tm-save">
            {busy ? "Adding..." : "Add Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", role: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setForm({ name: target.name || "", email: target.email || "", role: target.role, password: "" }); } }, [target]);
  const fixedRole = target && ["admin", "manager"].includes(target.role);
  const roleChanged = target && form.role !== target.role && CLIENT_ROLES.has(target.role);
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    if (!EMAIL_RE.test(form.email.trim())) { toast.error("Please enter a valid email address."); return; }
    if (form.password && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try { await api.put(`/users/${target.id}`, form); toast.success("Team member updated"); onClose(); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-xl" data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Edit Team Member</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Update their details. Leave password blank to keep the current one.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Name" required><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="edit-tm-name" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Email" required><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="edit-tm-email" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="New Password"><Input value={form.password} placeholder="Unchanged" onChange={e=>setForm({...form, password:e.target.value})} data-testid="edit-tm-password" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Role">
            {fixedRole ? <div className="h-10 flex items-center text-sm text-neutral-600">{ROLE_LABELS[target.role]}</div> : (
              <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="edit-tm-role"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                  {ROLES.map(r=><SelectItem key={r.key} value={r.key}>{r.label} · {r.group}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </F>
        </div>
        {roleChanged && <div className="text-[11px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2" data-testid="edit-tm-role-warning">Changing role removes them from clients assigned under their current role.</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="edit-tm-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="edit-tm-save">{busy ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignClientsDialog({ open, onOpenChange, user, clients, onSaved }) {
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ if (open && user) setIds(user.assigned_clients || []); }, [open, user]);
  const toggle = (cid) => setIds(v => v.includes(cid) ? v.filter(x=>x!==cid) : [...v, cid]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/users/${user.id}/assignments`, { client_ids: ids }); toast.success("Clients updated"); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-lg" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Assign Clients{user && <span className="ml-2 text-xs font-mono text-neutral-500">· {user.name} ({ROLE_LABELS[user.role]})</span>}
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Pick every client this member should see and work on.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] max-h-72 overflow-y-auto divide-y divide-[#E4E7EC]">
          {clients.length === 0 && <div className="p-3 text-xs text-neutral-500">No clients yet.</div>}
          {clients.map(c => (
            <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-white/[0.02] cursor-pointer" data-testid={`assign-client-${c.id}`}>
              <Checkbox checked={ids.includes(c.id)} onCheckedChange={()=>toggle(c.id)}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.client_name}</div>
                <div className="text-[10px] font-mono text-neutral-500 uppercase">{c.industry} · {c.package}</div>
              </div>
              <StatusPill status={c.status}/>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="assign-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="assign-save">
            {busy ? "Saving..." : "Save Assignments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, children}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{label}{required && <span className="text-rose-400 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}
